// Turns API payloads into chart/tile inputs (kept pure so they can be unit-tested).

import type { Activation, Delta, Proportion, RetentionGroup } from '@growth/client';
import { type BarItem, type LineSeries, ordinal } from './components/charts.tsx';
import type { TileDelta } from './components/ui.tsx';
import { fmtDeltaPct, fmtInt, fmtPct, fmtPp } from './format.ts';

export const MIN_SAMPLE = 30;

/** "IC 95 %: 21,0 %–33,4 % · n=150" */
export function ciText(p: Proportion | null | undefined): string {
  if (!p || p.rate === null) return 'sin muestra';
  return `IC 95 % ${fmtPct(p.ci_low)}–${fmtPct(p.ci_high)} · n=${fmtInt(p.n)}`;
}

/** Relative change for a tile, hidden when either side is missing. upIsGood decides the tone. */
export function deltaOf(d: Delta | undefined, opts: { upIsGood?: boolean; vs: string; rate?: boolean } = { vs: 'periodo anterior' }): TileDelta | undefined {
  if (!d || d.delta === null || d.a === null || d.b === null) return undefined;
  const upIsGood = opts.upIsGood ?? true;
  const text = opts.rate ? fmtPp(d.delta) : d.delta_pct === null ? undefined : fmtDeltaPct(d.delta_pct);
  if (!text) return undefined;
  const tiny = opts.rate ? Math.abs(d.delta) < 0.0005 : d.delta_pct !== null && Math.abs(d.delta_pct) < 0.005;
  const tone = tiny ? 'neutral' : d.delta > 0 === upIsGood ? 'good' : 'bad';
  return { text: `${d.delta > 0 ? '▲' : d.delta < 0 ? '▼' : ''} ${text}`.trim(), tone, vs: opts.vs };
}

export type RetentionKind = 'classic' | 'rolling';

/** Retention curve with its 95% band; immature days are gaps (never zeros). */
export function retentionSeries(group: RetentionGroup, kind: RetentionKind, slotN: number, label?: string): LineSeries {
  const cells = group.days;
  return {
    key: `${group.group}-${kind}`,
    label: label ?? group.group,
    slot: slotN,
    values: cells.map((c) => (c.mature > 0 ? c[kind].rate : null)),
    band: { low: cells.map((c) => (c.mature > 0 ? c[kind].ci_low : null)), high: cells.map((c) => (c.mature > 0 ? c[kind].ci_high : null)) },
  };
}

export interface FunnelStage {
  label: string;
  rate: Proportion | null;
  count: number;
}

/** Activation funnel (5 ordinal stages: the validated ordinal ramp has 5 steps). */
export function funnelStages(a: Activation): FunnelStage[] {
  return [
    { label: 'Instalaciones', rate: null, count: a.installs },
    { label: 'Tutorial completado', rate: a.tutorial_completion_rate, count: a.tutorial_completion_rate.k },
    { label: '1.ª run iniciada', rate: a.first_run_start_rate, count: a.first_run_start_rate.k },
    { label: '1.ª run terminada', rate: a.first_run_completed_rate, count: a.first_run_completed_rate.k },
    { label: '2.ª run iniciada', rate: a.second_run_rate, count: a.second_run_rate.k },
  ];
}

export function funnelItems(a: Activation): BarItem[] {
  return funnelStages(a).map((s, i) => ({
    label: s.label,
    value: s.count,
    color: ordinal(i + 1),
    note: s.rate ? fmtPct(s.rate.rate) : undefined,
    tooltip: {
      title: s.label,
      rows: [
        { label: 'jugadores', value: fmtInt(s.count) },
        ...(s.rate ? [{ label: 'de las instalaciones', value: fmtPct(s.rate.rate) }] : []),
      ],
      note: s.rate ? ciText(s.rate) : undefined,
    },
  }));
}

/** Share of a total as a short note ("23 %"). */
export function shareNote(n: number, total: number): string | undefined {
  return total > 0 ? fmtPct(n / total, 0) : undefined;
}
